/**
 * features/finance/CashFlow.tsx
 * ------------------------------------------------------------------
 * Where the money actually went.
 *
 * The income statement answers "did we make a surplus". It cannot answer
 * "why is there no money in the bank", and for a school those come apart
 * constantly — a healthy surplus sitting entirely in unpaid fees, a term's
 * income received in advance, a vehicle bought outright.
 *
 * THE RECONCILIATION IS THE HEADLINE, not a footnote. Every figure here
 * comes from an actual movement on a cash account, so the three sections
 * MUST add up to the change in the bank. When they do, the statement is
 * arithmetically sound and the page says so; when they do not, that is the
 * first thing on the page. A cash flow statement that does not tie to the
 * bank is decoration.
 *
 * INTERNAL TRANSFERS ARE SHOWN AS EXCLUDED rather than left out silently.
 * Moving money between the school's own accounts is real movement in the
 * cash book and no flow at all, and a bursar who can see the figure knows
 * it was noticed rather than lost.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { Waves, ArrowRightLeft, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useGlData } from '@/features/finance/useGlData';
import {
  buildCashFlow, FLOW_SECTION_LABEL, FLOW_SECTION_HELP,
} from '@/lib/cashFlow';
import { downloadTablePdf, downloadCsv, type TableExport } from '@/lib/tableExport';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, ExportBar,
} from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Signed, and said in words: a reader should not have to decode a minus. */
const flow = (n: number) => (n < 0 ? `(${money(Math.abs(n))})` : money(n));

export default function CashFlow() {
  const year = new Date().getFullYear();
  const [fy, setFy] = useState(`FY${year}`);
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const gl = useGlData(fy);

  const cf = useMemo(
    () => buildCashFlow({ accounts: gl.accounts, journals: gl.journals, from, to }),
    [gl.accounts, gl.journals, from, to],
  );

  function exportRows(): TableExport<string[]> {
    const rows: string[][] = [['', 'Cash at the start', money(cf.openingCash)]];
    for (const s of cf.sections) {
      rows.push([FLOW_SECTION_LABEL[s.section].toUpperCase(), '', '']);
      for (const l of s.lines) rows.push([l.accountCode, l.accountName, flow(l.amount)]);
      rows.push(['', `Net ${FLOW_SECTION_LABEL[s.section].toLowerCase()}`, flow(s.total)]);
    }
    rows.push(['', 'Net movement in cash', flow(cf.classifiedTotal)]);
    rows.push(['', 'Cash at the end', money(cf.closingCash)]);
    if (cf.internalTransfers) {
      rows.push(['', 'Transfers between the school’s own accounts (excluded)',
        money(cf.internalTransfers)]);
    }
    return {
      title: `Cash Flow — ${from} to ${to}`,
      columns: [
        { header: '', text: (r: string[]) => r[0], width: 18 },
        { header: '', text: (r: string[]) => r[1], width: 80 },
        { header: 'Amount', text: (r: string[]) => r[2], align: 'right', width: 30 },
      ],
      rows,
      note: cf.reconciles
        ? undefined
        : `This statement does not tie to the bank — ${money(Math.abs(cf.unexplained))} is `
          + 'unexplained. Do not report off it until that is found.',
    };
  }

  if (gl.loading) return <div className="p-8 flex justify-center"><Spinner /></div>;

  const years = Array.from({ length: 5 }, (_, i) => `FY${year - i}`);

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Waves}
        title="Cash Flow"
        subtitle="Where the money went, built from the bank rather than from the surplus"
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            <select value={fy} onChange={(e) => setFy(e.target.value)} className={glassFieldClass('py-1.5')}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={glassFieldClass('py-1.5')} />
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={glassFieldClass('py-1.5')} />
            <ExportBar onPdf={() => downloadTablePdf(exportRows())} onCsv={() => downloadCsv(exportRows())} />
          </div>
        )}
      />

      {/* THE HEADLINE. Sound or not sound, said before anything is read. */}
      <div className={`rounded-xl border px-4 py-3 flex items-start gap-2 ${cf.reconciles
        ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/40'
        : 'border-red-200 bg-red-50 dark:border-red-900/60 dark:bg-red-950/40'}`}
      >
        {cf.reconciles
          ? <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
          : <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600 dark:text-red-400" />}
        <p className={`text-sm ${cf.reconciles
          ? 'text-emerald-800 dark:text-emerald-200'
          : 'text-red-800 dark:text-red-200'}`}
        >
          {cf.reconciles
            ? 'This statement ties to the bank: the three sections account for every pula '
              + 'the cash accounts moved over the period.'
            : `This statement does not tie to the bank — ${money(Math.abs(cf.unexplained))} of `
              + 'movement is unexplained. Do not report off it until that is found.'}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatChip icon={Waves} label="Cash at the start" value={money(cf.openingCash)} />
        <StatChip icon={Waves} label="Cash at the end" value={money(cf.closingCash)} />
        <StatChip
          icon={cf.netMovement >= 0 ? CheckCircle2 : AlertTriangle}
          label={cf.netMovement >= 0 ? 'Cash increased by' : 'Cash fell by'}
          value={money(Math.abs(cf.netMovement))}
        />
        <StatChip
          icon={ArrowRightLeft}
          label="Transfers between own accounts — excluded"
          value={money(cf.internalTransfers)}
        />
      </div>

      {cf.sections.map((s) => (
        <SoftCard key={s.section} pad="p-0" hover={false}>
          <PanelHeader
            icon={Waves}
            title={FLOW_SECTION_LABEL[s.section]}
            subtitle={FLOW_SECTION_HELP[s.section]}
          />
          <div className="p-5 overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {s.lines.map((l) => (
                  <tr key={l.accountCode} className="border-b border-gray-50 dark:border-gray-800">
                    <td className="py-2 pr-4 font-mono text-xs w-16">{l.accountCode}</td>
                    <td className="py-2 pr-4">{l.accountName}</td>
                    <td className={`py-2 text-right tabular-nums ${l.amount < 0
                      ? 'text-red-600 dark:text-red-400' : ''}`}
                    >
                      {flow(l.amount)}
                    </td>
                  </tr>
                ))}
                {s.lines.length === 0 && (
                  <tr><td className="py-2 text-gray-400" colSpan={3}>No cash moved this way.</td></tr>
                )}
                <tr className="font-semibold">
                  <td className="py-2 pr-4" colSpan={2}>
                    Net {FLOW_SECTION_LABEL[s.section].toLowerCase()}
                  </td>
                  <td className="py-2 text-right tabular-nums">{flow(s.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </SoftCard>
      ))}

      <SoftCard pad="p-5" hover={false}>
        <table className="w-full text-sm">
          <tbody>
            <tr>
              <td className="py-1.5">Cash at the start</td>
              <td className="py-1.5 text-right tabular-nums">{money(cf.openingCash)}</td>
            </tr>
            <tr>
              <td className="py-1.5">Net movement</td>
              <td className="py-1.5 text-right tabular-nums">{flow(cf.classifiedTotal)}</td>
            </tr>
            <tr className="font-semibold border-t-2 border-gray-200 dark:border-gray-700">
              <td className="py-2">Cash at the end</td>
              <td className="py-2 text-right tabular-nums">{money(cf.closingCash)}</td>
            </tr>
            {!cf.reconciles && (
              <tr className="text-red-600 dark:text-red-400">
                <td className="py-1.5">Unexplained</td>
                <td className="py-1.5 text-right tabular-nums">{flow(cf.unexplained)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </SoftCard>
    </div>
  );
}
